import React, { useState, useEffect, useMemo } from 'react';
import { List, Typography, Box } from '@mui/material';
import ContainerBox from 'components/ContainerBox';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { Prompt } from 'react-router';

import { api } from 'common/frontend/api';
import { Callable } from 'common/frontend/callable';
import { useTranslation } from 'services/localization';
import { getTypedKeys } from 'common/utils/objects';
import { getEmailTypeDetails } from './utils';
import { CUSTOMER_FACING_EMAILS } from 'common/modules/emails/constants';
import { CustomerFacingEmail, EmailSettings } from 'common/modules/emails/types';
import { getDefaultEmailSettings } from 'common/modules/emails/utils/getDefaultEmailSettings';
import { Languages } from 'common/types';
import { useHasFeature } from 'hooks/useHasFeature';

import * as UserSelectors from 'selectors/UserSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as NotificationActions from 'actions/NotificationActions';
import SetPageTitle from 'components/Portals/SetPageTitle';
import SaveButton from 'common/components/SaveButton';
import EmailSection from './EmailSection';
import SendTestEmailDialog from './SendTestEmailDialog';
import { switchUnreachable } from 'common/utils/common';
import { useDocument } from 'hooks/useDocument';
import Banner from 'components/Banner';

type FormValues = EmailSettings;

const emailDefaultSettings = getDefaultEmailSettings();

const EmailsTab = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const activeLocationId = useSelector(UserSelectors.userActiveLocationId);
	const locations = useSelector(ShopSelectors.activeShopAllLocations);
	const activeLocation = locations.find((l) => l.id === activeLocationId);
	const activeUserEmail = useSelector(UserSelectors.activeUserEmail);
	const activeShopName = useSelector(ShopSelectors.activeShopName);
	const { hasFeature } = useHasFeature();

	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [emailType, setEmailType] = useState<CustomerFacingEmail | null>(null);

	const { control, formState, reset, handleSubmit } = useForm<FormValues>({
		defaultValues: emailDefaultSettings,
		mode: 'onChange',
	});
	const { isSubmitting, isValid, isDirty } = formState;

	const doc = useMemo(() => {
		if (!activeLocation?.id) return null;
		return api().emailSettings.doc(activeLocation.id);
	}, [activeLocation?.id]);

	const { data, loading, error } = useDocument({ doc });

	useEffect(() => {
		if (!!data && !isDirty) {
			reset({ ...data.emails });
		}
	}, [data, reset, isDirty]);

	const handleSave = async (data: FormValues) => {
		if (!shopId || !activeLocation) return;
		await api().emailSettings.doc(activeLocation.id).set(
			{
				merchantId: shopId,
				locationId: activeLocation.id,
				emails: data,
			},
			{ merge: true },
		);
		reset(data);
	};

	const handleSendTestEmail = async (args: {
		emailType: CustomerFacingEmail;
		emailTo: string;
		language: Languages;
	}) => {
		const { emailType, emailTo: emailToSend, language } = args;
		try {
			await Callable.internal.sendEmailTest({
				emailType,
				emailToSend,
				language,
				merchantId: shopId,
				locationId: activeLocation?.id,
				shopName: activeShopName,
			});
			dispatch(
				NotificationActions.showNotification({
					variant: 'info',
					message: t('emails.test.messageSent', 'Message sent'),
				}),
			);
		} catch (e) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		} finally {
			setIsDialogOpen(false);
		}
	};

	const handleOpenDialog = (emailType: CustomerFacingEmail) => {
		setEmailType(emailType);
		setIsDialogOpen(true);
	};

	const isEmailTypeSupported = (emailType: CustomerFacingEmail) => {
		switch (emailType) {
			case 'instoreOrderContract':
				return hasFeature('CHECK_IN');
			case 'extensionLinkConfirmation':
				return hasFeature('BETA_EXTENSIONS');
			case 'depositCaptured':
			case 'depositReservationFailed':
			case 'depositReserved':
			case 'onlineOrderConfirmation':
			case 'adminOrderConfirmation':
				return true;
			default:
				return switchUnreachable(emailType);
		}
	};

	const emailOptions = getTypedKeys(CUSTOMER_FACING_EMAILS)
		.filter(isEmailTypeSupported)
		.map(getEmailTypeDetails(t));

	return (
		<>
			<SetPageTitle
				title={t('store.storeSettings', 'Store settings')}
				priority={2}
				rightContent={
					<SaveButton
						disabled={!isDirty || !isValid}
						loading={isSubmitting}
						onClick={handleSubmit(handleSave)}
						t={t}
					/>
				}
			/>
			<Prompt
				when={isDirty}
				message={() =>
					t(
						'common:prompts.unsavedChanges',
						'You have unsaved changes - are you sure you want to leave this page?',
					)
				}
			/>
			{!!error && (
				<Banner
					variant="danger"
					title={t(
						'emails.test.errorFetchingSettings',
						'An error occurred while fetching your emails settings. Please refresh the page.',
					)}
				/>
			)}
			<ContainerBox>
				<Box mb={2}>
					<Typography variant="h6">{t('emails.test.orders.heading', 'Orders')}</Typography>
					<Typography variant="body2">
						{t(
							'emails.test.orders.description',
							'Manage emails that are automatically sent to your customers',
						)}
					</Typography>
				</Box>
				<List>
					{emailOptions.map((details, idx) => (
						<Controller
							key={details.emailType}
							control={control}
							name={`${details.emailType}.enabled`}
							render={({ field }) => (
								<EmailSection
									{...details}
									checked={field.value}
									onClick={handleOpenDialog}
									disabled={loading}
									onToggle={field.onChange}
									noDivider={idx === emailOptions.length - 1}
								/>
							)}
						/>
					))}
				</List>
			</ContainerBox>
			<SendTestEmailDialog
				emailTo={activeUserEmail}
				details={emailType ? getEmailTypeDetails(t, emailType) : null}
				isOpen={isDialogOpen}
				onClose={() => setIsDialogOpen(false)}
				onConfirm={handleSendTestEmail}
			/>
		</>
	);
};

export default EmailsTab;
